/** Map a REEL&SHORTS project onto the video-editor timeline. */

import {
  BROLL_CARD_CAROUSEL,
  BROLL_CONTEXT_OVERLAY,
  BROLL_SERIAL_CARDS,
  brollTypeMeta,
  isArtifactBrollType,
  isLayoutBrollType,
  orderedSnippets,
  sanitizeBrollVisualStyle,
} from './reelsShortsBroll';
import {
  keepWindowsForSnippet,
  originalToCutTime,
  remapWordTimings,
  sanitizePauseCut,
  snippetDurationSeconds,
} from './reelsShortsPauseCut';
import {
  sanitizeTitleOverlay,
  sanitizeTitleStyle,
  titleClearRatio,
  titleLayerForOverlay,
  titleTemplateMeta,
} from './reelsShortsTitle';
import { snippetSelectedAudioUrl } from './reelsShortsAudioClean';
import { buildCarouselClips } from './cardCarousel';
import { sanitizeArtifactMotionStyle } from './artifactStickerMotion';

export function estimateWordTimestamps(text, durationSeconds) {
  const duration = Math.max(0.08, Number(durationSeconds) || 0);
  const words = String(text || '').split(/\s+/).filter((w) => w.trim());
  if (!words.length) return [];
  const totalChars = words.reduce((sum, w) => sum + Math.max(1, w.length), 0);
  let cursor = 0;
  const out = [];
  for (const word of words) {
    const share = Math.max(1, word.length) / totalChars;
    const span = Math.max(0.08, duration * share);
    const start = cursor;
    const end = Math.min(duration, cursor + span);
    out.push({
      word,
      start: Math.round(start * 1000) / 1000,
      end: Math.round(end * 1000) / 1000,
    });
    cursor = end;
  }
  if (out.length) {
    out[out.length - 1] = { ...out[out.length - 1], end: Math.round(duration * 1000) / 1000 };
  }
  return out;
}

export function findTriggerWindow(words, triggerText, clipStart, clipDuration) {
  const needle = String(triggerText || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const list = Array.isArray(words) ? words : [];
  const fallback = {
    startTime: clipStart,
    duration: Math.max(0.4, Math.min(Number(clipDuration) || 0, 2.5) || Number(clipDuration) || 1),
  };
  if (!needle || !list.length) return fallback;

  const tokens = list.map((w) => String(w.word || '').trim());
  const joined = tokens.join(' ').toLowerCase();
  const idx = joined.indexOf(needle);
  if (idx < 0) return fallback;

  let cursor = 0;
  let startW = 0;
  let endW = list.length - 1;
  const endIdx = idx + needle.length - 1;
  for (let i = 0; i < tokens.length; i += 1) {
    const next = cursor + tokens[i].length;
    if (cursor <= idx && idx < next + 1) startW = i;
    if (cursor <= endIdx && endIdx < next) endW = i;
    cursor = next + 1;
  }
  const start = clipStart + Number(list[startW]?.start || 0);
  const end = clipStart + Number(list[endW]?.end || list[startW]?.end || 0);
  return { startTime: start, duration: Math.max(0.35, end - start) };
}

function snippetVideoUrl(snip) {
  return snip?.video_s3_url || snip?.original_s3_url || '';
}

function snippetAudioUrl(snip) {
  return snippetSelectedAudioUrl(snip) || snip?.audio_s3_url || snip?.original_s3_url || '';
}

function overlayLayerForPosition(position, { titleClear = 0 } = {}) {
  const pos = String(position || 'center').toLowerCase();
  const height = 0.36;
  const layer = { x: 0.5, y: 0.5, width: 0.42, height, rotation: 0 };
  if (pos.includes('top')) layer.y = 0.2;
  if (pos.includes('bottom')) layer.y = 0.78;
  if (pos.includes('left')) layer.x = 0.22;
  if (pos.includes('right')) layer.x = 0.78;
  const clear = Number(titleClear) || 0;
  if (clear > 0 && !pos.includes('bottom')) {
    const top = layer.y - height / 2;
    if (top < clear) layer.y = clear + height / 2;
  }
  return layer;
}

export function sanitizeOverlayLayer(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const x = Number(raw.x);
  const y = Number(raw.y);
  const width = Number(raw.width ?? raw.w);
  const height = Number(raw.height ?? raw.h);
  if (![x, y, width, height].every(Number.isFinite) || width < 0.04 || height < 0.04) return null;
  const rotation = Number(raw.rotation);
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
    width: Math.min(1, Math.max(0.04, width)),
    height: Math.min(1, Math.max(0.04, height)),
    rotation: Number.isFinite(rotation) ? Math.min(180, Math.max(-180, rotation)) : 0,
  };
}

export function layoutPaneLayer(brollType, position) {
  const pos = String(position || '').toLowerCase();
  if (brollType === 'split_half') {
    return pos === 'right'
      ? { x: 0.25, y: 0.5, width: 0.5, height: 1, rotation: 0 }
      : { x: 0.75, y: 0.5, width: 0.5, height: 1, rotation: 0 };
  }
  if (brollType === 'split_half_horizontal') {
    return pos === 'bottom'
      ? { x: 0.5, y: 0.25, width: 1, height: 0.5, rotation: 0 }
      : { x: 0.5, y: 0.75, width: 1, height: 0.5, rotation: 0 };
  }
  if (brollType === 'triple_horizontal') {
    return { x: 0.5, y: 420 / 1920 / 2, width: 1, height: 420 / 1920, rotation: 0 };
  }
  return overlayLayerForPosition(position);
}

export function positionFromLayoutLayer(brollType, layer, fallback = '') {
  const box = sanitizeOverlayLayer(layer);
  if (!box) return fallback;
  if (brollType === 'split_half') return box.x >= 0.5 ? 'left' : 'right';
  if (brollType === 'split_half_horizontal') return box.y >= 0.5 ? 'top' : 'bottom';
  return fallback;
}

function brollsForSnippet(project, snippetId) {
  const list = resolveReelsEditorCut(project).broll_suggestions;
  if (!Array.isArray(list)) return [];
  return list.filter((row) => row?.snippet_id === snippetId);
}

/** Prefer Edit Plan mirror; fall back to legacy `editor_cut`. */
export function resolveReelsEditorCut(project) {
  const plan = project?.edit_plan;
  const legacy = project?.editor_cut && typeof project.editor_cut === 'object' ? project.editor_cut : {};
  if (!plan || typeof plan !== 'object') return legacy;
  const story = plan.story && typeof plan.story === 'object' ? plan.story : {};
  const cleanup = plan.cleanup && typeof plan.cleanup === 'object' ? plan.cleanup : {};
  const timeline = plan.timeline && typeof plan.timeline === 'object' ? plan.timeline : {};
  const ordered = Array.isArray(story.ordered_snippet_ids) ? story.ordered_snippet_ids : [];
  const broll = Array.isArray(timeline.broll) ? timeline.broll : [];
  const diagramInserts = Array.isArray(timeline.diagram_inserts)
    ? timeline.diagram_inserts
    : (Array.isArray(legacy.diagram_inserts) ? legacy.diagram_inserts : []);
  const avatarInserts = Array.isArray(timeline.avatar_inserts)
    ? timeline.avatar_inserts
    : (Array.isArray(legacy.avatar_inserts) ? legacy.avatar_inserts : []);
  // Prefer plan when it has a cut; otherwise keep legacy assets/order.
  if (!ordered.length && !broll.length && !diagramInserts.length && !avatarInserts.length && !Object.keys(legacy).length) {
    return {
      ordered_snippet_ids: [],
      broll_suggestions: [],
      diagram_inserts: [],
      avatar_inserts: [],
      pause_cut: cleanup.pause_cut || legacy.pause_cut,
      title_overlay: story.title_overlay || timeline.title_overlay || legacy.title_overlay,
      broll_visual_style: story.visual_style || legacy.broll_visual_style,
      rationale: story.rationale || legacy.rationale || '',
      source: plan.source || legacy.source || 'edit_plan',
      editing_mode: story.editing_mode || legacy.editing_mode,
      cleanup_level: cleanup.level || legacy.cleanup_level,
    };
  }
  return {
    ...legacy,
    ordered_snippet_ids: ordered.length ? ordered : (legacy.ordered_snippet_ids || []),
    broll_suggestions: broll.length ? broll : (legacy.broll_suggestions || []),
    diagram_inserts: diagramInserts.length ? diagramInserts : (legacy.diagram_inserts || []),
    avatar_inserts: avatarInserts.length ? avatarInserts : (legacy.avatar_inserts || []),
    pause_cut: cleanup.pause_cut || legacy.pause_cut,
    title_overlay: story.title_overlay || timeline.title_overlay || legacy.title_overlay,
    broll_visual_style: story.visual_style || legacy.broll_visual_style,
    rationale: story.rationale || legacy.rationale || '',
    source: plan.source || legacy.source || 'edit_plan',
    editing_mode: story.editing_mode || legacy.editing_mode,
    cleanup_level: cleanup.level || legacy.cleanup_level,
  };
}

export function reelsShortsEditorPath(projectId) {
  return projectId ? `/reels-shorts/${projectId}/video-editor` : '/reels-shorts/create';
}

export function padReelsOverlayTracks(tracks) {
  const lanes = Array.isArray(tracks)
    ? tracks.map((lane) => (Array.isArray(lane) ? lane : []))
    : [];
  while (lanes.length < 3) lanes.push([]);
  return lanes;
}

export function reelsOverlayLaneMeta(trackIdx) {
  if (trackIdx === 0) {
    return {
      label: 'B-ROLL',
      emptyHint: 'Half-screen / triple-slice layouts from the AI editor land here',
      accent: '#22d3ee',
      testId: 'reels-broll-timeline-track',
    };
  }
  if (trackIdx === 1) {
    return {
      label: 'Artifacts',
      emptyHint: 'Context overlays and serial cards land here, timed to spoken words',
      accent: '#c084fc',
      testId: 'reels-artifact-timeline-track',
    };
  }
  if (trackIdx === 2) {
    return {
      label: 'Title',
      emptyHint: 'Hook title burned onto the top of the reel',
      accent: '#facc15',
      testId: 'reels-title-timeline-track',
    };
  }
  return {
    label: 'Overlay',
    emptyHint: '',
    accent: '#f59e0b',
    testId: 'host-overlay-timeline-track',
  };
}

function overlayClipName(row, fallbackLabel) {
  const meta = brollTypeMeta(row.broll_type);
  const label = fallbackLabel || meta?.label || row.broll_type || 'B-ROLL';
  const prompt = row.generation_prompt || row.trigger_text || row.asset_kind || '';
  return prompt ? `${label} · ${prompt}` : label;
}

function mapTriggerToCut(timed, windows, blockStart) {
  const localStart = Number(timed?.startTime) || 0;
  const localEnd = localStart + Math.max(0.08, Number(timed?.duration) || 0.4);
  const mappedStart = originalToCutTime(localStart, windows);
  const mappedEnd = originalToCutTime(localEnd, windows);
  return {
    startTime: blockStart + mappedStart,
    duration: Math.max(0.35, mappedEnd - mappedStart),
  };
}

function makeOverlayClip({
  id,
  name,
  snippetId,
  startTime,
  duration,
  row,
  reelsLane,
  titleClear = 0,
}) {
  const savedLayer = sanitizeOverlayLayer(row.layer);
  const defaultLayer = reelsLane === 'broll'
    ? layoutPaneLayer(row.broll_type, row.position)
    : overlayLayerForPosition(row.position, {
      titleClear: reelsLane === 'artifact' ? titleClear : 0,
    });
  return {
    id,
    name,
    sceneId: snippetId,
    suggestionId: row.id || '',
    overlayKind: 'host',
    startTime,
    duration,
    imageUrl: row.preview_url || row.image_url || '',
    brollType: row.broll_type,
    reelsLane,
    triggerText: row.trigger_text || '',
    displayText: row.display_text || '',
    generationPrompt: row.generation_prompt || '',
    generatedMedia: row.generated_media || '',
    visualStyle: row.visual_style ? sanitizeBrollVisualStyle(row.visual_style) : '',
    hasBackground: row.has_background != null
      ? Boolean(row.has_background)
      : reelsLane !== 'artifact',
    ...(reelsLane === 'artifact'
      ? { animationStyle: sanitizeArtifactMotionStyle(row.animation_style) }
      : {}),
    sticker: reelsLane === 'artifact',
    borderRadius: 0,
    layer: savedLayer || defaultLayer,
  };
}

export function reelsShortsProjectToEditorState(project, editorId) {
  const cut = resolveReelsEditorCut(project);
  const projectForOrder = { ...project, editor_cut: cut };
  const snippets = orderedSnippets(projectForOrder);
  const pauseCut = sanitizePauseCut(cut.pause_cut);
  const titleOverlay = sanitizeTitleOverlay(cut.title_overlay, project?.title);
  const titleClear = titleClearRatio(titleOverlay);
  const videoClips = [];
  const audioClips = [];
  const brollClips = [];
  const artifactClips = [];
  const wordCues = [];
  const scenes = [];
  let cursor = 0;

  snippets.forEach((snip, index) => {
    const sourceDuration = Math.max(0.5, snippetDurationSeconds(snip) || Number(snip.duration_seconds) || 3);
    const transcript = String(snip.transcript || '').trim();
    const sourceWords = (Array.isArray(snip.word_timestamps) && snip.word_timestamps.length)
      ? snip.word_timestamps
      : estimateWordTimestamps(transcript, sourceDuration);
    const windows = keepWindowsForSnippet(
      { ...snip, duration_seconds: sourceDuration, word_timestamps: sourceWords },
      pauseCut,
    );
    const cutDuration = Math.max(
      0.25,
      windows.reduce((sum, win) => sum + Math.max(0, win.end - win.start), 0),
    );
    const words = remapWordTimings(sourceWords, windows);
    const videoUrl = snippetVideoUrl(snip);
    const audioUrl = snippetAudioUrl(snip);
    const name = snip.filename || `Clip ${index + 1}`;
    const blockStart = cursor;

    windows.forEach((win, wIdx) => {
      const dur = Math.max(0.25, win.end - win.start);
      const clipId = windows.length === 1 ? snip.id : `${snip.id}-cut-${wIdx}`;
      videoClips.push({
        id: clipId,
        name: windows.length === 1 ? name : `${name} · ${wIdx + 1}`,
        sceneId: snip.id,
        videoUrl: videoUrl || null,
        imageUrl: null,
        audioUrl: audioUrl || null,
        startTime: cursor,
        duration: dur,
        playbackDuration: dur,
        trimStart: win.start,
        trimEnd: win.end,
        videoSourceDurationMax: sourceDuration,
        videoMediaDurationSeconds: sourceDuration,
        videoPlaybackSpeed: 1,
        muted: true,
        volume: 0,
        sceneText: transcript,
        narrationText: transcript,
        wordTimestamps: words,
        sceneColorIndex: index,
      });
      if (audioUrl) {
        audioClips.push({
          id: `audio-${clipId}`,
          name: windows.length === 1 ? `${name} narration` : `${name} narration · ${wIdx + 1}`,
          sceneId: snip.id,
          url: audioUrl,
          startTime: cursor,
          duration: dur,
          sourceDuration: sourceDuration,
          audioSourceTrimStart: win.start,
          volume: 0.9,
          muted: false,
        });
      }
      cursor += dur;
    });

    // Group 2–3 words into caption cards (same heuristic as backend burn-in).
    const groups = [];
    let buf = [];
    let chars = 0;
    words.forEach((word) => {
      const token = String(word.word || '').trim();
      if (!token) return;
      const extra = token.length + (buf.length ? 1 : 0);
      if (buf.length && (buf.length >= 3 || chars + extra > 20)) {
        groups.push(buf);
        buf = [word];
        chars = token.length;
      } else {
        buf.push(word);
        chars += extra;
      }
    });
    if (buf.length) groups.push(buf);
    if (groups.length) {
      groups.forEach((group, gIdx) => {
        const start = Number(group[0]?.start) || 0;
        const end = Number(group[group.length - 1]?.end) || start + 0.2;
        wordCues.push({
          id: `subtitle-group-${snip.id}-${gIdx}`,
          audioClipId: `audio-${windows.length === 1 ? snip.id : `${snip.id}-cut-0`}`,
          sceneId: snip.id,
          startTime: blockStart + start,
          duration: Math.max(0.12, end - start),
          text: group.map((w) => w.word).filter(Boolean).join(' '),
          muted: false,
          groupIndex: gIdx,
        });
      });
    } else {
      words.forEach((word, wIdx) => {
        const localStart = Number(word.start) || 0;
        const localEnd = Number(word.end) || localStart + 0.12;
        wordCues.push({
          id: `subtitle-word-${snip.id}-${wIdx}`,
          audioClipId: `audio-${windows.length === 1 ? snip.id : `${snip.id}-cut-0`}`,
          sceneId: snip.id,
          startTime: blockStart + localStart,
          duration: Math.max(0.08, localEnd - localStart),
          text: word.word || '',
          muted: false,
          wordIndex: wIdx,
        });
      });
    }

    brollsForSnippet(project, snip.id).forEach((row, bIdx) => {
      if (row.broll_type === BROLL_CARD_CAROUSEL) {
        const cardRows = Array.isArray(row.cards) && row.cards.length ? row.cards : [];
        const carouselCards = cardRows.map((card, cIdx) => {
          const trigger = card.trigger_text || row.trigger_text || '';
          const timed = trigger
            ? findTriggerWindow(sourceWords, trigger, 0, sourceDuration)
            : { startTime: (sourceDuration / Math.max(1, cardRows.length)) * cIdx, duration: 1 };
          const mapped = mapTriggerToCut(timed, windows, blockStart);
          return {
            image: card.preview_url || card.image_url || '',
            title: card.label || card.trigger_text || `Card ${cIdx + 1}`,
            timestamp: mapped.startTime,
          };
        });
        artifactClips.push({
          id: `card-carousel-${snip.id}-${bIdx}`,
          name: overlayClipName(row, 'Card carousel'),
          sceneId: snip.id,
          suggestionId: row.id || '',
          overlayKind: 'host',
          startTime: blockStart,
          duration: cutDuration,
          brollType: BROLL_CARD_CAROUSEL,
          reelsLane: 'artifact',
          triggerText: row.trigger_text || '',
          displayText: row.display_text || '',
          generationPrompt: row.generation_prompt || '',
          carouselCards,
          sticker: false,
          borderRadius: 0,
          layer: { x: 0.5, y: 0.82, width: 0.92, height: 0.28, rotation: 0 },
        });
        return;
      }
      if (row.broll_type === BROLL_SERIAL_CARDS) {
        const cardRows = Array.isArray(row.cards) && row.cards.length ? row.cards : null;
        const cards = cardRows
          || [{ ...row, label: overlayClipName(row, 'Serial Cards'), trigger_text: row.trigger_text }];
        const items = cards.map((card, cIdx) => ({
          id: `serial-${cIdx}`,
          number: String(cIdx + 1),
          title: card.label || card.trigger_text || `Card ${cIdx + 1}`,
          // Each card owns its asset. Falling back to the row's would paint one card's
          // generated image (and its words) onto every other card in the rail.
          imageUrl: card.preview_url || card.image_url
            || (cardRows ? '' : row.preview_url || row.image_url || ''),
        }));
        const groupClips = buildCarouselClips({
          items,
          playhead: blockStart,
          aspectRatio: '9:16',
          params: { layout: 'horizontal', animationIn: 'popIn', staggerDelay: 0.25, animationDuration: 0.4 },
          holdDuration: Math.max(1.4, cutDuration),
        });
        groupClips.forEach((clip, cIdx) => {
          const card = cards[cIdx] || {};
          const trigger = card.trigger_text || row.trigger_text || '';
          const timed = trigger
            ? findTriggerWindow(sourceWords, trigger, 0, sourceDuration)
            : { startTime: 0, duration: sourceDuration };
          const mapped = mapTriggerToCut(timed, windows, blockStart);
          artifactClips.push({
            ...clip,
            id: `artifact-card-${snip.id}-${bIdx}-${cIdx}`,
            sceneId: snip.id,
            suggestionId: row.id || '',
            cardId: card.id || '',
            cardIndex: cardRows ? cIdx : null,
            brollType: BROLL_SERIAL_CARDS,
            reelsLane: 'artifact',
            triggerText: trigger,
            displayText: card.label || '',
            generationPrompt: card.generation_prompt || row.generation_prompt || '',
            generatedMedia: card.generated_media || (cardRows ? '' : row.generated_media || ''),
            visualStyle: (card.visual_style || row.visual_style)
              ? sanitizeBrollVisualStyle(card.visual_style || row.visual_style)
              : '',
            hasBackground: Boolean(
              card.has_background != null ? card.has_background : row.has_background,
            ),
            imageUrl: items[cIdx]?.imageUrl || clip.imageUrl,
            startTime: mapped.startTime,
            duration: Math.max(0.8, mapped.duration, clip.duration * 0.5),
            ...(sanitizeOverlayLayer(row.layer) ? { layer: sanitizeOverlayLayer(row.layer) } : {}),
          });
        });
        return;
      }

      const timed = findTriggerWindow(
        sourceWords,
        row.trigger_text || '',
        0,
        sourceDuration,
      );
      const mapped = mapTriggerToCut(timed, windows, blockStart);

      if (row.broll_type === BROLL_CONTEXT_OVERLAY || isArtifactBrollType(row.broll_type)) {
        artifactClips.push(makeOverlayClip({
          id: `artifact-${snip.id}-${bIdx}`,
          name: overlayClipName(row, 'Overlay'),
          snippetId: snip.id,
          startTime: mapped.startTime,
          duration: mapped.duration,
          row,
          reelsLane: 'artifact',
          titleClear,
        }));
        return;
      }

      if (isLayoutBrollType(row.broll_type)) {
        brollClips.push(makeOverlayClip({
          id: `broll-${snip.id}-${bIdx}`,
          name: overlayClipName(row),
          snippetId: snip.id,
          startTime: blockStart,
          duration: cutDuration,
          row,
          reelsLane: 'broll',
        }));
      }
    });

    scenes.push({
      id: snip.id,
      scene_number: index + 1,
      title: name,
      scene_text: transcript,
      narration_text: transcript,
      word_timestamps: words,
      video_url: snip.video_s3_url || snip.original_s3_url || null,
      audio_url: snip.audio_s3_url || null,
      audio_duration: cutDuration,
      duration: cutDuration,
      duration_seconds: cutDuration,
    });
  });

  const total = cursor || Number(project?.total_duration) || 5;
  const titleClips = [];
  if (titleOverlay.enabled && (titleOverlay.line1 || titleOverlay.line2)) {
    const meta = titleTemplateMeta(titleOverlay.template_id);
    const label = [titleOverlay.line1, titleOverlay.line2].filter(Boolean).join(' · ');
    titleClips.push({
      id: 'title-overlay',
      name: label || meta?.label || 'Title',
      sceneId: snippets[0]?.id || 'title',
      overlayKind: 'host',
      startTime: 0,
      duration: total,
      imageUrl: '',
      brollType: 'title_overlay',
      reelsLane: 'title',
      triggerText: '',
      titleLine1: titleOverlay.line1,
      titleLine2: titleOverlay.line2,
      titleTemplateId: titleOverlay.template_id,
      titleStyle: titleOverlay.style,
      layer: sanitizeOverlayLayer(cut.title_overlay?.layer) || titleLayerForOverlay(titleOverlay),
    });
  }
  return {
    story: {
      id: editorId,
      title: project?.title || 'Untitled reel',
      app: 'reels_shorts',
      story_type: 'reels_shorts',
      input: project?.input || { language: project?.language, aspect_ratio: '9:16' },
      language: project?.language || 'auto',
      final_video_url: project?.final_video_url || null,
      total_duration: total,
      aspect_ratio: project?.aspect_ratio || '9:16',
    },
    scenes,
    videoTracks: [videoClips],
    audioTracks: audioClips.length ? [audioClips] : [[]],
    musicTracks: [[]],
    overlayTracks: padReelsOverlayTracks([brollClips, artifactClips, titleClips]),
    subtitleTracks: wordCues.length ? [wordCues] : [[]],
    exportAspectRatio: '9:16',
  };
}

function clipMatchesSuggestion(clip, row) {
  if (!clip || !row) return false;
  if (row.id && String(clip.suggestionId || '') === String(row.id)) return true;
  return String(clip.sceneId || '') === String(row.snippet_id || '')
    && String(clip.brollType || '') === String(row.broll_type || '');
}

function clipMatchesCard(clip, card, idx) {
  if (!clip) return false;
  if (card?.id && String(clip.cardId || '') === String(card.id)) return true;
  return clip.cardId ? false : Number(clip.cardIndex) === idx;
}

function applyClipToSuggestion(row, clips) {
  const matches = clips.filter((clip) => clipMatchesSuggestion(clip, row));
  if (!matches.length) return row;
  const cards = row.broll_type === BROLL_SERIAL_CARDS && Array.isArray(row.cards)
    ? row.cards
    : null;
  const clip = matches[0];
  const next = { ...row };
  const layer = sanitizeOverlayLayer(clip.layer);
  if (layer) next.layer = layer;
  // A card clip's copy belongs to its card. The row's display_text / generation_prompt drive
  // the whole rail, so writing one card's words there makes every card generate that text.
  if (!cards?.length) {
    if (clip.displayText != null && String(clip.displayText).trim()) {
      next.display_text = String(clip.displayText).trim().slice(0, 400);
    }
    if (clip.generationPrompt != null && String(clip.generationPrompt).trim()) {
      next.generation_prompt = String(clip.generationPrompt).trim().slice(0, 800);
    }
    if (clip.triggerText != null && String(clip.triggerText).trim()) {
      next.trigger_text = String(clip.triggerText).trim().slice(0, 400);
    }
  }
  if (clip.visualStyle) {
    next.visual_style = sanitizeBrollVisualStyle(clip.visualStyle);
  }
  if (clip.hasBackground != null) {
    next.has_background = Boolean(clip.hasBackground);
  }
  if (row.broll_type === BROLL_CONTEXT_OVERLAY || clip.reelsLane === 'artifact') {
    next.animation_style = sanitizeArtifactMotionStyle(clip.animationStyle);
  }
  if (isLayoutBrollType(row.broll_type)) {
    next.position = positionFromLayoutLayer(row.broll_type, clip.layer, row.position);
  }
  if (cards) {
    next.cards = cards.map((card, idx) => {
      const cardClip = matches.find((m) => clipMatchesCard(m, card, idx));
      if (!cardClip) return card;
      const label = String(cardClip.cardTitle || cardClip.displayText || card.label || '').trim();
      const prompt = String(cardClip.generationPrompt || '').trim();
      const trigger = String(cardClip.triggerText || '').trim();
      return {
        ...card,
        ...(label ? { label: label.slice(0, 160) } : {}),
        ...(prompt ? { generation_prompt: prompt.slice(0, 800) } : {}),
        ...(trigger ? { trigger_text: trigger.slice(0, 400) } : {}),
        ...(cardClip.visualStyle
          ? { visual_style: sanitizeBrollVisualStyle(cardClip.visualStyle) }
          : {}),
        ...(cardClip.hasBackground != null
          ? { has_background: Boolean(cardClip.hasBackground) }
          : {}),
      };
    });
  }
  return next;
}

export function editorOverlaysToCutPatch(overlayTracks, editorCut = {}) {
  const clips = padReelsOverlayTracks(overlayTracks).flat().filter(Boolean);
  const suggestions = Array.isArray(editorCut?.broll_suggestions)
    ? editorCut.broll_suggestions.map((row) => applyClipToSuggestion(row, clips))
    : [];
  const titleClip = clips.find((clip) => (
    String(clip.brollType || '') === 'title_overlay' || String(clip.reelsLane || '') === 'title'
  ));
  const title_overlay = {
    ...(editorCut?.title_overlay || {}),
    ...(titleClip ? {
      enabled: true,
      line1: String(titleClip.titleLine1 || '').slice(0, 80),
      line2: String(titleClip.titleLine2 || '').slice(0, 80),
      template_id: titleClip.titleTemplateId || (editorCut?.title_overlay || {}).template_id,
      style: sanitizeTitleStyle(titleClip.titleStyle || (editorCut?.title_overlay || {}).style),
      layer: sanitizeOverlayLayer(titleClip.layer),
    } : {}),
  };
  return { broll_suggestions: suggestions, title_overlay };
}
